/* Collection page: filters, sort and "Load more" without page reloads.
   <lmb-collection> wraps the Collection products section. When a chip, the sort or an option in the Filter & sort
   panel changes, it fetches the section for the new URL (Section Rendering API: <url>?section_id=<id>), swaps
   the parts marked data-region, and updates the address bar, so filtered pages can be shared and Back works.
   "Load more" fetches the next page the same way and appends its products.
   Every chip, the sort and "Load more" is also a plain link or form, so nothing breaks before this loads. */
if (!customElements.get('lmb-collection')) {
  const REGIONS = ['chips', 'showing', 'sort', 'sort-phone', 'sheet', 'sheet-foot', 'grid', 'more'];

  customElements.define(
    'lmb-collection',
    class LmbCollection extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.onClick = this.onClick.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onPopState = this.onPopState.bind(this);
        this.addEventListener('click', this.onClick);
        this.addEventListener('change', this.onChange);
        this.addEventListener('submit', this.onSubmit);
        window.addEventListener('popstate', this.onPopState);
        this.renderedUrl = this.withoutHash(window.location.href);
      }

      disconnectedCallback() {
        window.removeEventListener('popstate', this.onPopState);
      }

      get sheet() {
        return this.querySelector('lmb-overlay.fsheet');
      }

      onClick(event) {
        const link = event.target.closest('a[data-filter-link]');
        if (link && this.contains(link)) {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
          event.preventDefault();
          this.update(link.href);
          return;
        }
        const more = event.target.closest('a[data-load-more]');
        if (more) {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
          event.preventDefault();
          this.loadMore(more);
        }
      }

      onChange(event) {
        const target = event.target;
        if (target.matches('[data-sort-select]')) {
          const url = new URL(window.location.href);
          url.searchParams.set('sort_by', target.value);
          url.searchParams.delete('page');
          this.update(url.toString(), { focusId: target.id });
          return;
        }
        const form = target.closest('form[data-filter-form]');
        if (!form) return;
        // Price ranges are exclusive: Shopify's price filter is one range.
        if (target.name === 'price_band' && target.checked) {
          form.querySelectorAll('input[name="price_band"]').forEach((input) => {
            if (input !== target) input.checked = false;
          });
        }
        this.update(this.urlFromForm(form), { focusId: target.id });
      }

      onSubmit(event) {
        const form = event.target;
        if (form.matches('[data-filter-form]')) {
          // Changes are applied as they're made; "Show n" just closes the panel.
          event.preventDefault();
          if (this.sheet) this.sheet.close();
        } else if (form.matches('[data-sort-form]')) {
          event.preventDefault();
          const select = form.querySelector('[data-sort-select]');
          if (select) select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      withoutHash(href) {
        const url = new URL(href, window.location.origin);
        url.hash = '';
        return url.toString();
      }

      /* Back/Forward between filter states. A jump to an anchor on the same page (e.g. the skip link) also fires
         popstate; the products shown (including "Load more" pages) stay as they are then. */
      onPopState() {
        const url = new URL(window.location.href);
        if (url.pathname !== this.dataset.path) return;
        if (this.withoutHash(url) === this.renderedUrl) return;
        this.update(url.toString(), { push: false });
      }

      urlFromForm(form) {
        const url = new URL(form.getAttribute('action'), window.location.origin);
        const params = new URLSearchParams();
        for (const [key, value] of new FormData(form)) {
          if (key === 'price_band' || value === '') continue;
          if (key === 'sort_by' && value === form.dataset.defaultSort) continue;
          params.append(key, value);
        }
        const band = form.querySelector('input[name="price_band"]:checked');
        if (band) {
          if (band.dataset.gte) params.set('filter.v.price.gte', band.dataset.gte);
          if (band.dataset.lte) params.set('filter.v.price.lte', band.dataset.lte);
        }
        url.search = params.toString();
        return url.toString();
      }

      sectionUrl(url) {
        const target = new URL(url, window.location.origin);
        target.searchParams.set('section_id', this.sectionId);
        return target.toString();
      }

      setBusy(busy) {
        this.classList.toggle('is-loading', busy);
        const grid = this.querySelector('[data-region="grid"]');
        if (grid) grid.setAttribute('aria-busy', String(busy));
      }

      swap(doc, name) {
        const current = this.querySelector(`[data-region="${name}"]`);
        const next = doc.querySelector(`[data-region="${name}"]`);
        if (current && next) current.replaceWith(next);
      }

      announce() {
        const status = this.querySelector('[data-status]');
        const showing = this.querySelector('.cmore__count') || this.querySelector('[data-region="showing"]');
        if (status) status.textContent = showing ? showing.textContent.trim() : '';
      }

      /* Re-renders the toolbar, panel, grid and "Load more" for a new filter/sort URL. */
      async update(url, { push = true, focusId } = {}) {
        if (this.controller) this.controller.abort();
        this.controller = new AbortController();
        const active = document.activeElement;
        const activeId = focusId || (active && this.contains(active) ? active.id : null);
        const hadFocus = active && this.contains(active) && active !== document.body;
        const inSheet = hadFocus && !!active.closest('.fsheet');
        this.setBusy(true);
        try {
          const doc = await LMB.fetchHTML(this.sectionUrl(url), { signal: this.controller.signal });
          REGIONS.forEach((name) => this.swap(doc, name));
          const clean = new URL(url, window.location.origin);
          clean.searchParams.delete('section_id');
          clean.searchParams.delete('page');
          if (push) window.history.pushState({ lmbCollection: true }, '', clean.toString());
          this.renderedUrl = this.withoutHash(window.location.href);
          // Swapped parts replace the focused element: put focus back on its new copy (same id), or nearby.
          if (hadFocus && !this.contains(document.activeElement)) {
            let el = activeId ? document.getElementById(activeId) : null;
            if (!el || !this.contains(el)) {
              el = inSheet ? this.querySelector('.fsheet__close') : this.querySelector('.ctb__filter, .ctb__sort-phone');
            }
            if (el) el.focus({ preventScroll: true });
          }
          this.announce();
          this.setBusy(false);
        } catch (error) {
          if (error.name === 'AbortError') return;
          window.location.href = url;
        }
      }

      /* Appends the next page's products and moves keyboard focus to the first new one. */
      async loadMore(button) {
        if (button.getAttribute('aria-busy') === 'true') return;
        button.setAttribute('aria-busy', 'true');
        try {
          const doc = await LMB.fetchHTML(this.sectionUrl(button.href));
          const grid = this.querySelector('[data-grid]');
          const cells = Array.from(doc.querySelectorAll('[data-grid] > [data-product-cell]'));
          if (!grid || !cells.length) throw new Error('No products');
          grid.append(...cells);
          this.swap(doc, 'more');
          this.swap(doc, 'showing');
          // A product card's link, or a page/article tile on search.
          const firstLink = cells[0].querySelector('.card__row, .msearch__text');
          if (firstLink) firstLink.focus({ preventScroll: true });
          this.announce();
        } catch (error) {
          window.location.href = button.href;
        }
      }
    }
  );
}
